// src/Pages/SessionReportDetail/index.jsx
import { ArrowBack, EventNote } from '@mui/icons-material';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import sessionsApi from 'Api/axios/sessions-api';
import Breadcrumb from 'Components/Breadcrumb/Breadcrumb';
import { Padding } from 'Components/Padding';
import { colors } from 'Constants/Colors';
import SideBarContainer from 'Containers/SideBarContainer';
import useTranslation from 'Hooks/useTranslation';
import SessionStatusPill from 'Pages/EditCourseSessions/Components/SessionStatusPill';
import { formatSessionDate, formatSessionTime } from 'Pages/EditCourseSessions/utils/session-datetime';
import ReportsLayout from 'Pages/reports/components/ReportsLayout/ReportsLayout';
import ReportsTitle from 'Pages/reports/components/ReportsTitle/ReportsTitle';
import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { downloadFile } from 'Utils/file-utils';
import AttendanceFilters from './Components/attendance-filters';
import AttendanceKpisCards from './Components/attendance-kpis-cards';
import AttendanceTableReadOnly from './Components/attendance-table-read-only';
import useSessionReportDetail from './Hooks/use-session-report-detail';
import { IconToggleGroup } from 'Components/IconToggleGroup';
import { CoursesReportIcon, EmployeesReportIcon } from 'Pages/reports/components/icons';

const styles = {
  headerMeta: { display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' },
  subline: { fontSize: 12.5, color: colors.GRAY_DARK },
  description: { fontSize: 12.5, color: colors.GRAY_DARK, mt: 1, mb: 1 },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    py: 8,
    gap: 1.5,
  },
  emptyIcon: { fontSize: 48, color: colors.GRAY_DARK },
};

const buildSubline = session => {
  if (!session) return '';
  const parts = [];
  if (session.course?.name) parts.push(session.course.name);
  if (session.startDateTime) {
    parts.push(
      `${formatSessionDate(session.startDateTime)} ${formatSessionTime(session.startDateTime)}` +
        (session.endDateTime ? `–${formatSessionTime(session.endDateTime)}` : '')
    );
  }
  if (session.instructorName || session.instructor?.fullName) {
    parts.push(session.instructor?.fullName || session.instructorName);
  }
  if (session.location) parts.push(session.location);
  return parts.join(' · ');
};

export default function SessionReportDetailPage() {
  const { id } = useParams();
  const history = useHistory();
  const { t2 } = useTranslation('SessionsReport');

  const { session, stats, attendances, pagination, filters, setFilters, setPage, setPageSize, loading, notFound } =
    useSessionReportDetail(id);

  const [exporting, setExporting] = useState(false);
  const goBack = () => history.push('/reporte/sesiones/cursos');

  const viewMode = 'courses';
  const viewModeOptions = [
    { value: 'employees', tooltip: 'Por empleados', icon: <EmployeesReportIcon /> },
    { value: 'courses', tooltip: 'Por cursos', icon: <CoursesReportIcon /> },
  ];
  const handleViewModeChange = value => {
    history.push(value === 'employees' ? '/reporte/sesiones/empleados' : '/reporte/sesiones/cursos');
  };
  const hasActiveFilters = !!filters.search || filters.status !== 'all';

  const handleExport = async () => {
    if (!session || exporting) return;
    setExporting(true);
    const res = await sessionsApi.exportAttendance(id, {
      search: filters.search?.trim() || undefined,
      status: filters.status === 'all' ? undefined : filters.status,
    });
    setExporting(false);
    if (res.error) {
      toast.error(t2('detail.errorExport'));
      return;
    }
    if (res.data) {
      const safeTitle = (session.title || 'sesion').replace(/[^a-zA-Z0-9-_]+/g, '_').slice(0, 50);
      downloadFile(res.data, `Asistencia_${safeTitle}_${Date.now()}.xlsx`);
      toast.success(t2('detail.exportSuccess'));
    }
  };

  if (notFound) {
    return (
      <SideBarContainer>
        <Padding padding="1rem">
          <Box sx={styles.emptyState}>
            <EventNote sx={styles.emptyIcon} />
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{t2('detail.notFoundTitle')}</Typography>
            <Button onClick={goBack} startIcon={<ArrowBack />} sx={{ textTransform: 'none' }}>
              {t2('detail.notFoundCta')}
            </Button>
          </Box>
        </Padding>
      </SideBarContainer>
    );
  }

  const accent = session?.title ? `"${session.title}"` : '';

  return (
    <SideBarContainer>
      <Padding padding="1rem 1rem 0 1rem">
        <Breadcrumb
          items={[
            { label: `${t2('pageTitle')} ${t2('pageAccent')}`, path: '/reporte/sesiones/cursos' },
            { label: t2('detail.breadcrumb'), path: `/reporte/sesiones/${id}` },
          ]}
        />
      </Padding>
      <ReportsLayout
        title={<ReportsTitle title={t2('detail.title')} accent={accent} />}
        onExport={session ? handleExport : undefined}
        buttons={
          <IconToggleGroup
            options={viewModeOptions}
            value={viewMode}
            onChange={handleViewModeChange}
            ariaLabel="Modo de vista"
          />
        }
      />
      <Padding padding="0 1rem 1rem 1rem">
        <Box sx={styles.headerMeta}>
          {loading && !session ? (
            <Skeleton variant="text" width={280} height={20} />
          ) : (
            <Typography sx={styles.subline}>{buildSubline(session)}</Typography>
          )}
          {session && <SessionStatusPill status={session.status} variant="session" />}
        </Box>

        {!loading && session?.description && <Typography sx={styles.description}>{session.description}</Typography>}

        <AttendanceKpisCards stats={stats} loading={loading} />

        <AttendanceFilters
          values={filters}
          onChange={setFilters}
          shown={attendances.length}
          total={pagination.totalItems}
        />

        <AttendanceTableReadOnly
          rows={attendances}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          loading={loading}
          hasActiveFilters={hasActiveFilters}
        />
      </Padding>
    </SideBarContainer>
  );
}
